import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { margin, MarginProps } from 'styled-system'

import { Box, ErrorMessage, Flex } from 'components'
import InlineInputButtons from 'components/Buttons/InlineInputButtons/v2'
import Spinner from 'components/Spinner'

import useClickOutSide from 'hooks/use-click-outside-hooks'
import { FixMeAny } from 'types/global'
import { KeyCode } from 'utils/constants/keyCode'
import { TextInputProps } from 'components/TextInput'
import { colors } from 'utils/colors'
import InlineEditBtn from 'modules/Leads/LeadDetailV2/shared/InlineEditBtn'

type TooltipProps = {
  title: string
  position: 'top' | 'bottom' | 'left' | 'right' | 'bottomRight' | 'topRight' | 'topLeft' | 'bottomLeft'
  textWidth: string
}

export type InlineEditableInputProps = MarginProps & {
  disabled?: boolean
  disableConfirm?: boolean
  fieldProps: TextInputProps
  tooltipProps?: TooltipProps
  confirmButton?: React.ReactNode
  cancelButton?: React.ReactNode
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, toggleEdit?: VoidFunction) => void
  onConfirmClick?: (value: string, toggleEdit?: VoidFunction) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onCancelClick?: () => void
  onLabelClick?: () => void
  children?: JSX.Element
  readOnly?: boolean
  isLoading?: boolean
  confirmOnEnter?: boolean
  saveOnClickOutside?: boolean
  displayNode?: React.ReactNode
  label?: string
  buttonPosition?: 'bottom' | 'right'
  isDuplicateCheckRequired?: boolean
}

const ClickWrapper: React.FC<{ onClick: (e: React.ChangeEvent<HTMLInputElement>) => void }> = ({
  children,
  onClick,
}) => (
  <Box position="relative">
    {React.Children.map(children, (child: FixMeAny) =>
      React.cloneElement(child, {
        onClick: onClick,
      }),
    )}
  </Box>
)

const InlineEditableInput: React.FC<InlineEditableInputProps> = (props) => {
  const [displayEdit, setDisplayEdit] = useState(false)

  const {
    fieldProps,
    disabled,
    disableConfirm,
    children,
    confirmButton,
    cancelButton,
    onBlur,
    onCancelClick,
    onConfirmClick,
    onLabelClick,
    onChange,
    readOnly,
    isLoading = false,
    confirmOnEnter = true,
    saveOnClickOutside = true,
    displayNode,
    buttonPosition = 'right',
    isDuplicateCheckRequired = false,
  } = props

  const marginProps = margin(props)
  const { error, customError, onKeyDown, ...otherFieldProps } = fieldProps
  const [isEditing, setIsEditing] = React.useState(false)

  const inlineInputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (error) {
      setIsEditing(true)
    }
  }, [error])

  useClickOutSide(inlineInputRef, () => {
    if (isEditing) {
      if (error == '' || !error) {
        if (saveOnClickOutside) {
          !disableConfirm && onConfirmClick?.(fieldProps?.value as string)
        } else {
          onCancelClick?.()
        }
        setIsEditing(false)
      }
    }
  })

  const handleConfirmationClick = useCallback(() => {
    onConfirmClick?.(fieldProps?.value as string, isDuplicateCheckRequired ? () => setIsEditing(false) : () => {})
  }, [fieldProps, onConfirmClick, isDuplicateCheckRequired])

  const handleCancelClick = useCallback(() => {
    onCancelClick?.()
  }, [onCancelClick])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> & React.KeyboardEvent<HTMLTextAreaElement>) => {
      e.stopPropagation()
      onKeyDown?.(e)

      if (e.key === KeyCode.enter && confirmOnEnter) {
        handleConfirmationClick()
        setIsEditing(false)
        return
      }

      if (e.key === KeyCode.escape) {
        handleCancelClick()
        setIsEditing(false)
      }
    },
    [confirmOnEnter, handleCancelClick, handleConfirmationClick, onKeyDown],
  )

  const newFieldProps = useMemo(
    () => ({
      ...otherFieldProps,
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        onBlur?.(e, () => setIsEditing(false))
      },
      onChange,
      onKeyDownCapture: handleKeyDown,
    }),
    [handleKeyDown, onBlur, onChange, otherFieldProps],
  )

  const renderInlineEditButtons = () => (
    <Flex justifyContent="flex-end" alignItems="flex-end" gap="0px">
      <InlineInputButtons
        position="relative"
        customCancel={cancelButton}
        customConfirm={confirmButton}
        isConfirmDisabled={disableConfirm}
        onClickCancel={() => {
          handleCancelClick()
          setIsEditing(false)
        }}
        onClickConfirm={() => {
          handleConfirmationClick()
          if (!fieldProps.error && !isDuplicateCheckRequired) setIsEditing(false)
        }}
      />
    </Flex>
  )

  if (readOnly) {
    return <>{displayNode ?? children}</>
  }

  const errorComponent = customError ? customError : <>{error && <ErrorMessage>{error}</ErrorMessage>}</>

  return (
    <Container {...marginProps} ref={inlineInputRef} isEditing={isEditing}>
      {isEditing ? (
        <>
          <Flex
            padding="8px 16px"
            flexWrap="wrap"
            width="100%"
            justifyContent="space-between"
            alignItems="flex-end"
            gap="8px"
          >
            <Box flexGrow={1}>{children && <>{React.cloneElement(children, { ...newFieldProps })}</>}</Box>
            {buttonPosition == 'right' && renderInlineEditButtons()}
            {errorComponent}
          </Flex>
          {buttonPosition == 'bottom' && (
            <Flex padding="0px 16px 8px 16px" justifyContent="flex-end" gap="0">
              {renderInlineEditButtons()}
            </Flex>
          )}
        </>
      ) : (
        <Box onMouseEnter={() => setDisplayEdit(true)} onMouseLeave={() => setDisplayEdit(false)}>
          <ClickWrapper
            onClick={(e) => {
              if (disabled) return

              onLabelClick?.()
              e.stopPropagation()
            }}
          >
            <LabelWrapper>
              {displayNode}
              {displayEdit && !disabled && (
                <Box
                  transform="translateY(-50%)"
                  position="absolute"
                  right={0}
                  top="50%"
                  backgroundColor="white"
                  onClick={(e) => {
                    if (disabled) return

                    onLabelClick?.()
                    setIsEditing(true)
                    setDisplayEdit(false)
                    e.stopPropagation()
                  }}
                >
                  <InlineEditBtn />
                </Box>
              )}
            </LabelWrapper>
          </ClickWrapper>
        </Box>
      )}
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
    </Container>
  )
}

const LabelWrapper = styled.div<MarginProps & { width?: number | 'auto' }>`
  ${margin}

  *:hover {
    background: ${({ theme }) => theme.colors.gray1};
  }

  &:hover {
    input,
    textarea {
      background: ${({ theme }) => theme.colors.gray1};
      border-color: ${({ theme }) => theme.colors.blue1};
    }
  }
`
export const EditBtn = styled.button`
  height: 33px;
  background: linear-gradient(${colors.gray1}, ${colors.gray1} 80%);
`

const Container = styled.div<MarginProps & { isEditing?: boolean }>`
  ${margin}
  position: relative;
  ${({ theme, isEditing }) => isEditing && `border-bottom: 1px solid ${theme.colors.gray3}`};
`

const LoadingWrapper = styled.div`
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-right: 1rem;
  padding-bottom: 0.7rem;
`

export default InlineEditableInput
